import Cookies from 'js-cookie'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { UIButton } from 'src/components/ui/Button'
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE_KEY,
  SUPPORTED_LOCALES,
  type SupportedLocaleCodes,
} from 'src/i18n/constants'
import { normalizeLocale } from 'src/i18n/utils'
import {
  resolveLocaleFromUrl,
  setLocaleToCookies,
  setLocaleToUrl,
} from 'src/stores/locale'

function detectNavigatorLocale(): SupportedLocaleCodes | null {
  const lang = navigator.language?.toLowerCase()
  return (normalizeLocale(lang) ??
    normalizeLocale(lang?.split('-')[0]) ??
    null) as SupportedLocaleCodes | null
}

function LocaleSuggestionToastContent({
  onClose,
  suggestedLocale,
}: {
  onClose: () => void
  suggestedLocale: SupportedLocaleCodes
}) {
  const { t } = useTranslation(['common', 'action'], { lng: suggestedLocale })
  const localeName = SUPPORTED_LOCALES[suggestedLocale].label

  function confirm() {
    setLocaleToCookies(suggestedLocale)
    onClose()
    setLocaleToUrl(suggestedLocale)
  }

  function dismiss() {
    const { resolved: urlLocale } = resolveLocaleFromUrl()
    setLocaleToCookies(urlLocale ?? DEFAULT_LOCALE)
    onClose()
  }

  return (
    <div className="flex flex-col gap-2" lang={suggestedLocale}>
      <p className="text-sm">
        <Trans
          t={t}
          i18nKey="common:locale-suggestion.content"
          components={{ strong: <strong /> }}
          values={{ locale: localeName }}
        />
      </p>
      <div className="flex gap-2 w-full items-center justify-between">
        <UIButton
          variant="ghost"
          size="small"
          className="text-sm px-2 py-[5px]"
          onPress={dismiss}
        >
          {t('action:cancel')}
        </UIButton>
        <UIButton variant="filled" size="small" onPress={confirm}>
          {t('common:locale-suggestion.switch-to.label', {
            locale: localeName,
          })}
        </UIButton>
      </div>
    </div>
  )
}

export function LocaleSuggestionToast() {
  useEffect(() => {
    if (Cookies.get(LOCALE_COOKIE_KEY)) return

    const navLocale = detectNavigatorLocale()
    if (!navLocale) return

    const { resolved: urlLocale } = resolveLocaleFromUrl()
    if (navLocale === (urlLocale ?? DEFAULT_LOCALE)) return

    const id = toast(
      ({ closeToast }) => (
        <LocaleSuggestionToastContent
          onClose={closeToast!}
          suggestedLocale={navLocale}
        />
      ),
      { autoClose: false, closeButton: false, draggable: false },
    )

    return () => {
      toast.dismiss(id)
    }
  }, [])

  return null
}
